import { useState } from 'react';
import styles from './EmbeddedVideo.module.scss';

import { YoutubeEmbed } from '../YoutubeVideo';
import useDictionary from 'lib/hooks/useDictionary';
import CtaLink from '../../Atoms/Cta/CtaLink';
import EnhancedNextImage from '../../Atoms/EnhancedNextImage/EnhancedNextImage';
import OneTrustFilter from '../../OneTrust/OneTrustFilter';
import useEmbeddedVideo from 'lib/hooks/video/useEmbeddedVideo';
import { TiktokEmbed } from '../TiktokVideo';
import { InstagramEmbed } from '../InstagramVideo';

export interface IEmbeddedVideoProps {
  videoId?: string;
  videoTitle: string;
  videoThumbnailUrl?: string;
  videoWithAudioDescriptionURL?: string;
  videoTranscriptFileUrl?: string;
  isPortrait: boolean;
  videoUrl?: string;
  videoType?: string;
  ctaLinksLeftAligned?: boolean;
}

export default function EmbeddedVideo({
  videoId,
  videoTitle,
  videoThumbnailUrl,
  isPortrait,
  videoWithAudioDescriptionURL,
  videoTranscriptFileUrl,
  videoUrl,
  videoType,
  ctaLinksLeftAligned = false,
}: IEmbeddedVideoProps) {
  let finalVideoId = videoId ?? '';
  let finalVideoUrl = videoUrl ?? '';
  let finalVideoTitle = videoTitle ?? '';
  let finalVideoThumbnailUrl = videoThumbnailUrl ?? '';
  let finalIsPortrait = isPortrait;
  let finalVideoType = videoType ?? '';
  let finalCtaLinksLeftAligned = ctaLinksLeftAligned;
  // Translations
  const { getDictionaryValue } = useDictionary();

  // Manage inline video state (for non-modal videos)
  const [showInlineVideo, setShowInlineVideo] = useState(false);

  if (videoId && !videoUrl) {
    // Set the url if we can determine it from the videoId
    // We need to do this before call to useEmbeddedVideo hook so we have a url to fetch oEmbed data from
    if (finalVideoType === 'YouTube' || finalVideoType === 'YouTube Shorts') {
      finalVideoUrl = `https://www.youtube.com/watch?v=${videoId}`;
    } else if (finalVideoType === 'TikTok') {
      finalVideoUrl = `https://www.tiktok.com/embed/${videoId}`;
    } else if (finalVideoType === 'Instagram') {
      finalVideoUrl = `https://www.instagram.com/reel/${videoId}/`;
    }
  }
  // Use custom hook to possibly override videoId and thumbnail
  const {
    extractedIsPortrait,
    extractedVideoType,
    extractedVideoId,
    extractedVideoThumbnail,
    oEmbedData,
  } = useEmbeddedVideo(finalVideoUrl);

  // Return early if no videoId or videoUrl is provided
  if (!videoId && !finalVideoUrl) {
    return <></>;
  }

  if (finalVideoUrl || !videoId) {
    finalVideoId = extractedVideoId ?? '';
    finalVideoThumbnailUrl = videoThumbnailUrl || extractedVideoThumbnail || '';
    finalIsPortrait = isPortrait || extractedIsPortrait;
    finalVideoType = videoType || extractedVideoType;
    finalVideoTitle = videoTitle || oEmbedData?.title || '';
  }

  const handlePlayClick = () => {
    setShowInlineVideo(true);
  };

  if (ctaLinksLeftAligned && !finalIsPortrait) {
    finalCtaLinksLeftAligned = true;
  }

  return (
    <>
      <div
        className={`printHide ${styles.videoSection} ${finalIsPortrait ? styles.videoSectionPortrait : styles.videoSectionLandscape}`}
      >
        <div className={styles.videoContainer}>
          <OneTrustFilter
            filteredComponent={
              <>
                {finalVideoThumbnailUrl && !showInlineVideo && (
                  <div className={styles.videoSubContainer}>
                    <button
                      aria-label={`${getDictionaryValue('Helpers.EmbeddedVideo.PlayButton') || 'Play'} ${finalVideoTitle}`}
                      className={styles.videoPlayButton}
                      onClick={handlePlayClick}
                    ></button>
                    <EnhancedNextImage
                      src={finalVideoThumbnailUrl}
                      alt={`${getDictionaryValue('Helpers.EmbeddedVideo.ThumbnailAlt') || 'Thumbnail'} ${finalVideoTitle}`}
                      fill
                      style={{ objectFit: 'fill' }}
                    />
                  </div>
                )}

                {/* Inline video (when not using modal) */}
                {finalVideoId && (showInlineVideo || !finalVideoThumbnailUrl) && (
                  <div className={styles.inlineVideoContainer}>
                    {(() => {
                      switch (finalVideoType) {
                        case 'YouTube':
                        case 'YouTube Shorts':
                          return (
                            <YoutubeEmbed
                              videoId={finalVideoId}
                              videoTitle={finalVideoTitle}
                              videoUrl={finalVideoUrl}
                              autoplay={finalVideoThumbnailUrl ? true : false}
                              isPortrait={finalIsPortrait}
                            />
                          );
                        case 'TikTok':
                          return (
                            <TiktokEmbed videoId={finalVideoId} iframeTitle={finalVideoTitle} />
                          );
                        case 'Instagram':
                          return (
                            <InstagramEmbed
                              videoId={finalVideoId}
                              iframeTitle={finalVideoTitle}
                              videoUrl={finalVideoUrl}
                            />
                          );
                        // Add more cases for other video types if needed
                        default:
                          return null;
                      }
                    })()}
                  </div>
                )}
              </>
            }
            placeholderFeatureType="video"
            optanonGroups={['2', '3', '4']}
            enablePlaceholder={true}
          />
        </div>

        {(videoWithAudioDescriptionURL || videoTranscriptFileUrl) && (
          <ul
            className={`${styles.videoLinks} ${finalCtaLinksLeftAligned ? styles.ctaLeftAligned : ''}`}
            aria-label={
              getDictionaryValue('Helpers.EmbeddedVideo.AccessibilityOptions') ||
              'Video Accessibility Options'
            }
          >
            {videoWithAudioDescriptionURL && (
              <li className={styles.videoLinksItem}>
                <CtaLink
                  url={videoWithAudioDescriptionURL}
                  target="_blank"
                  ctaVariant="brand"
                  ctaStyle="link"
                  text={
                    getDictionaryValue('Helpers.EmbeddedVideo.VideoWithAudioDescription') ||
                    'Video with Audio Description'
                  }
                />
              </li>
            )}
            {videoTranscriptFileUrl && (
              <li className={styles.videoLinksItem}>
                <CtaLink
                  url={videoTranscriptFileUrl}
                  target="_blank"
                  ctaVariant="brand"
                  ctaStyle="link"
                  text={
                    getDictionaryValue('Helpers.EmbeddedVideo.TextTranscript') ||
                    'Text transcript (TXT)'
                  }
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </>
  );
}
